import React, { createContext, useContext, useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';

export type SubscriptionTier = 'free' | 'basic' | 'family' | 'provider';

interface SubscriptionState {
  tier: SubscriptionTier;
  isActive: boolean;
  expiryDate: Date | null;
  features: string[];
}

interface SubscriptionContextType {
  subscription: SubscriptionState;
  updateSubscription: (tier: SubscriptionTier) => void;
  cancelSubscription: () => void;
  hasFeature: (feature: string) => boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const DEFAULT_FEATURES: Record<SubscriptionTier, string[]> = {
  free: ['basic_access'],
  basic: ['basic_access', 'personal_assessment'],
  family: ['basic_access', 'personal_assessment', 'family_management'],
  provider: ['basic_access', 'patient_management', 'advanced_analytics'],
};

export const SubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
  const [subscription, setSubscription] = useState<SubscriptionState>({
    tier: 'free',
    isActive: true,
    expiryDate: null,
    features: DEFAULT_FEATURES.free,
  });
  
  const { toast } = useToast();

  const updateSubscription = useCallback((tier: SubscriptionTier) => {
    const expiryDate = new Date();
    expiryDate.setMonth(expiryDate.getMonth() + 1);
    
    setSubscription({
      tier,
      isActive: true,
      expiryDate,
      features: DEFAULT_FEATURES[tier],
    });

    toast({
      title: "Subscription Updated",
      description: `Your subscription has been updated to ${tier}`,
    });
  }, [toast]);

  const cancelSubscription = useCallback(() => {
    setSubscription(prev => ({
      ...prev,
      isActive: false,
      tier: 'free',
      features: DEFAULT_FEATURES.free,
    }));

    toast({
      title: "Subscription Cancelled",
      description: "Your subscription has been cancelled",
      variant: "destructive",
    });
  }, [toast]);

  const hasFeature = useCallback((feature: string) => {
    return subscription.isActive && subscription.features.includes(feature);
  }, [subscription]);

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      updateSubscription,
      cancelSubscription,
      hasFeature,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};